import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { featuredCars } from '@/mocks/cars';

export default function FeaturedCars() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActive(index);
      setIsTransitioning(false);
    }, 300);
  };

  const next = () => goTo((active + 1) % featuredCars.length);
  const prev = () => goTo((active - 1 + featuredCars.length) % featuredCars.length);

  useEffect(() => {
    intervalRef.current = setInterval(next, 5000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [active]);

  const car = featuredCars[active];

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`${t('featured.whatsapp')} ${car.brand} ${car.model} ${car.year}. ${t('featured.whatsapp2')}`);
    window.open(`https://wa.me/34631611077?text=${msg}`, '_blank');
  };

  return (
    <section id="destacados" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background Images */}
      {featuredCars.map((c, i) => (
        <div
          key={c.id}
          className={`absolute inset-0 transition-opacity duration-700 ${i === active ? 'opacity-100' : 'opacity-0'}`}
        >
          <img
            src={c.image}
            alt={`${c.brand} ${c.model}`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/95 via-[#050505]/60 to-[#050505]/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-transparent" />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-20">
        {/* Section label */}
        <div className="inline-flex items-center gap-2 border border-purple-500/30 px-4 py-2 rounded-full mb-10">
          <span className="text-purple-400 text-xs tracking-widest uppercase font-medium">{t('featured.label')}</span>
        </div>

        <div className={`transition-all duration-300 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
          {/* Brand & Model */}
          <div className="mb-4">
            <span className="font-label text-purple-400 text-sm tracking-widest uppercase">
              {car.brand}
            </span>
          </div>
          <h2 className="font-display text-5xl sm:text-7xl lg:text-9xl xl:text-[10rem] text-white mb-4 leading-none" style={{ letterSpacing: '0.02em' }}>
            {car.model}
          </h2>
          <div className="font-label text-white/40 text-sm tracking-widest mb-10">{car.year}</div>

          {/* Specs */}
          <div className="flex flex-wrap gap-3 sm:gap-6 mb-8 sm:mb-10">
            <div className="glass-card rounded-xl px-4 sm:px-6 py-3 sm:py-4">
              <div className="font-label text-white/40 text-xs tracking-widest uppercase mb-1">{t('featured.speed')}</div>
              <div className="font-display text-2xl sm:text-3xl gradient-text-purple">{car.speed}</div>
            </div>
            <div className="glass-card rounded-xl px-4 sm:px-6 py-3 sm:py-4">
              <div className="font-label text-white/40 text-xs tracking-widest uppercase mb-1">{t('featured.power')}</div>
              <div className="font-display text-2xl sm:text-3xl gradient-text-purple">{car.power}</div>
            </div>
            <div className="glass-card rounded-xl px-4 sm:px-6 py-3 sm:py-4">
              <div className="font-label text-white/40 text-xs tracking-widest uppercase mb-1">{t('featured.accel')}</div>
              <div className="font-display text-2xl sm:text-3xl gradient-text-purple">{car.acceleration}</div>
            </div>
            <div className="glass-card rounded-xl px-4 sm:px-6 py-3 sm:py-4">
              <div className="font-label text-white/40 text-xs tracking-widest uppercase mb-1">{t('featured.origin')}</div>
              <div className="font-display text-2xl sm:text-3xl text-white">{car.origin}</div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleWhatsApp}
            className="gradient-purple text-white font-semibold tracking-wider uppercase px-8 py-4 rounded-full glow-purple hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-whatsapp-line mr-2" />
            {t('featured.cta')}
          </button>
        </div>

        {/* Navigation */}
        <div className="absolute bottom-6 right-4 sm:bottom-10 sm:right-10 flex items-center gap-3 sm:gap-4">
          <div className="flex gap-2 mr-4">
            {featuredCars.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`transition-all duration-300 rounded-full cursor-pointer ${
                  i === active ? 'w-8 h-2 gradient-purple' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                }`}
              />
            ))}
          </div>

          <button
            onClick={prev}
            className="w-12 h-12 flex items-center justify-center glass-card rounded-full hover:border-purple-500/50 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-white" />
          </button>
          <button
            onClick={next}
            className="w-12 h-12 flex items-center justify-center gradient-purple rounded-full glow-purple hover:scale-105 transition-all duration-300 cursor-pointer"
          >
            <i className="ri-arrow-right-line text-white" />
          </button>
        </div>

        {/* Car counter */}
        <div className="absolute top-10 right-4 sm:right-10 font-display text-white/15 text-6xl sm:text-8xl select-none" style={{ letterSpacing: '0.05em' }}>
          0{active + 1}
        </div>
      </div>
    </section>
  );
}